import { Close } from "assets/icons/Close";
import { Button } from "components/Button";
import { SubmitHandler, useForm } from "react-hook-form";
import { campaignsApi } from "screens/Campaigns/service";
import { ETaskType, IProject, ITaskForm } from "screens/Campaigns/types";
import { ProjectFormInput } from "screens/DApps/components/Pannel/components/ProjectFormInput";
import { TypesDropdown } from "../TypesDropdown";
import { ProjectsDropdown } from "../ProjectsDropdown";
import { useCampaignsStore } from "screens/Campaigns/store";
import { useEffect, useState } from "react";

export const TasksAddForm = ({
  isOpen,
  onClose,
  getAllTasksHandler
}: {
  isOpen: boolean;
  onClose: () => void;
  getAllTasksHandler: () => void;
}) => {
  const { handleSubmit, watch, reset, control, setValue } = useForm<ITaskForm>({
    defaultValues: {
      title: "",
      order: 1,
      type: null
    },
    mode: "all"
  });
  const { projects, hasMoreProjects, setProjects } = useCampaignsStore();
  const { selectedTask } = useCampaignsStore().TASKS;
  const [projectsPage, setProjectsPage] = useState(1);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const availableTypes = Object.values(ETaskType);
  const selectedProject = watch("projectId");
  const selectedType = watch("type");
  const guildId = watch("guildId");
  const roleId = watch("roleId");
  const channelLink = watch("channelLink");

  const showTaskSourceField =
    selectedType &&
    selectedType !== ETaskType.FOLLOW &&
    selectedType !== ETaskType.JOIN_DS_CHANNEL;
  const showJoinTaskFields =
    selectedType &&
    selectedType === ETaskType.JOIN_DS_CHANNEL;

  const handleMoreProjectsClick = async () => {
    if (!hasMoreProjects || isLoadingProjects) return;
    setIsLoadingProjects(true);
    try {
      const newPage = projectsPage + 1;
      const newProjects = await campaignsApi.getAllProjects(newPage);
      if (newProjects) {
        setProjects([...projects, ...newProjects]);
        setProjectsPage(newPage);
      }
    } catch (error) {
      console.error("Error loading more projects", error);
    } finally {
      setIsLoadingProjects(false);
    }
  };
  // Fill form with selected task data when it changes
  useEffect(() => {
    if (selectedTask && isOpen) {
      setValue("title", selectedTask.title || "");
      setValue("order", selectedTask.order || 0);
      setValue("type", selectedTask.type);
      setValue("projectId", selectedTask.project.id || "");
      setValue("source", selectedTask.source || "");
      setValue("guildId", selectedTask.guild_id || "");
      setValue("roleId", selectedTask.role_id || "");
      setValue("channelLink", selectedTask.channel_link || "");
    }
  }, [selectedTask, isOpen, setValue]);

  // Reset form when form is closed
  useEffect(() => {
    if (!isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  const onSubmit: SubmitHandler<ITaskForm> = (data) => {
    if (selectedTask) {
      // Update existing task
      campaignsApi.updateTask({ ...data, id: selectedTask.id }, () => {
        reset();
        onClose();
        getAllTasksHandler();
      });
    } else {
      // Create new task
      campaignsApi.createTask(data, () => {
        reset();
        onClose();
        getAllTasksHandler();
      });
    }
  };

  const isSourceRequired = selectedType !== ETaskType.FOLLOW && selectedType !== ETaskType.JOIN_DS_CHANNEL;
  const isDsFieldsRequired = selectedType === ETaskType.JOIN_DS_CHANNEL;
  const isSubmitDisabled =
    !watch("order") ||
    !watch("title") ||
    !watch("type") ||
    (isSourceRequired && !watch("source")) ||
    (isDsFieldsRequired && (!guildId || !roleId || !channelLink));

  const handleTypeClick = (type: ETaskType) => {
    setValue("type", type);
  };

  const onProjectClickHandler = (project: IProject) => {
    setValue("projectId", project.id);
  };

  return (
    <>
      {isOpen && (
        <div
          className="fixed z-4 bg-black/40 top-0 left-0 right-0 bottom-0 w-full"
          onClick={onClose}
        />
      )}
      <div
        className={`fixed w-full md:w-[487px] h-[100svh] overflow-y-auto custom-scrollbar px-[20px] md:px-[40px] pt-[60px] top-0 bottom-0 right-0 bg-black md:border-l-[1px] z-5 transform transition-transform duration-300 ${isOpen ? "translate-x-0" : "translate-x-full"
          }`}
      >
        <div className="flex text-[24px] font-[700] leading-[32px] justify-between items-center mb-[40px]">
          <h3>{selectedTask ? "Edit task" : "Add new task"}</h3>
          <button onClick={onClose}>
            <Close />
          </button>
        </div>

        <form
          id="task-add-form"
          onSubmit={handleSubmit(onSubmit)}
          className={`flex flex-col gap-[24px] h-fit`}
        >
          <ProjectsDropdown
            projects={projects}
            onProjectClick={(project) => onProjectClickHandler(project)}
            hasMore={hasMoreProjects}
            isLoading={isLoadingProjects}
            onLoadMoreClick={handleMoreProjectsClick}
          >
            <div className="capitalize cursor-pointer bg-[#FFFFFF1A] custom-scrollbar overflow-x-auto h-[43px] rounded-[8px] flex items-center gap-1 px-[20px]">
              {selectedProject &&
                projects.find((project) => project.id === selectedProject)
                  ?.title}
              {!selectedProject && (
                <span className="text-[#FFFFFF80]">Select project</span>
              )}
            </div>
          </ProjectsDropdown>
          <ProjectFormInput
            title="Task title"
            placeholder="Add title here"
            control={control}
            name="title"
          />
          <ProjectFormInput
            title="Order"
            placeholder="Add order here"
            control={control}
            name="order"
            type="number"
          />

          <div>
            <p className="ml-1 mb-1 text-[12px] leading-[17px] font-[500] text-[#FFFFFF99]">
              Type
            </p>

            <TypesDropdown
              availableTypes={availableTypes}
              onTypeClick={(ecosystem) =>
                handleTypeClick(ecosystem as ETaskType)
              }
            >
              <div className="capitalize cursor-pointer bg-[#FFFFFF1A] custom-scrollbar overflow-x-auto h-[43px] rounded-[8px] flex items-center gap-1 px-[20px]">
                {selectedType && selectedType}
                {!selectedType && (
                  <span className="text-[#FFFFFF80]">Select type</span>
                )}
              </div>
            </TypesDropdown>
          </div>

          {showTaskSourceField && (
            <ProjectFormInput
              title="Task source"
              placeholder="https://"
              control={control}
              name="source"
            />
          )}

          {showJoinTaskFields && (
            <div className="flex flex-col gap-3">
              <ProjectFormInput
                title="Discord guild ID"
                placeholder="123456789012345678"
                control={control}
                name="guildId"
                disabled={!!selectedTask}
              />

              <ProjectFormInput
                title="Discord invite link"
                placeholder="https://discord.gg/your-invite-link"
                control={control}
                name="channelLink"
                disabled={!!selectedTask}
              />

              <ProjectFormInput
                title="Discord role ID"
                placeholder="123456789012345678"
                control={control}
                name="roleId"
                disabled={!!selectedTask}
              />
            </div>
          )}
        </form>

        <div className="bg-gradient-to-t from-black to-transparent pb-[40px] pt-[60px] flex items-center w-full left-0 right-0 z-10">
          <Button
            extraClass="w-full"
            type="submit"
            form="task-add-form"
            disabled={isSubmitDisabled}
          >
            <span className="mr-[10px] text-[18px]">
              {selectedTask ? "✓" : "+"}
            </span>{" "}
            {selectedTask ? "Update task" : "Add new task"}
          </Button>
        </div>
      </div>
    </>
  );
};
